import { inject, Injectable } from '@angular/core';
import { MatterportComponent } from '@simlab/matterport';
import {
  ComponentConfiguration,
  SpriteComponent,
  SpriteConfiguration
} from '@simlab/simlab-facility-management/scene-object';
import { AssetFacade } from '@simOn/asset';
import { AssetTooltipComponent } from '@simOn/asset/element/ui';
import { AssetCategoryMarkerIconService, MarkerIcons } from '@simOn/asset-categories/data-access';
import { AssetWalkFiltersState } from '@simOn/asset/element/data-access';
import { TAsset } from '@simOn/asset/modifying/models';
import { TComponentController } from '@simOn/common/scan';
import {
  catchError,
  combineLatest,
  concatAll,
  defer,
  filter,
  iif,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';
import { Vector3 } from 'three';
import { ComponentControllerBase } from './component-controller-base';
@Injectable()
export class AssetComponentsControllerService extends ComponentControllerBase implements TComponentController {
  private readonly _assetFacade = inject(AssetFacade);
  private readonly _markerIconService = inject(AssetCategoryMarkerIconService);
  /** Present only on the 3D walk; other scan hosts render markers unfiltered. */
  private readonly _walkFilters = inject(AssetWalkFiltersState, { optional: true });
  readonly _mode = ['assets'];
  override get mode() {
    return this._mode;
  }
  public set selectedMode(value: string) {
    this.mode.includes(value) ? this._showAll() : this._hideAll();
  }

  readonly _nodes: Record<string, MatterportComponent> = {};
  readonly components$: Observable<void> = defer(() => this.selectedModel).pipe(
    switchMap(() =>
      combineLatest([
        this._assetFacade.selectAllAssets$,
        this._walkFilters ? this._walkFilters.assetFilter$ : of(() => true)
      ]).pipe(
        map(([assets, assetFilter]: [TAsset[], (asset: TAsset) => boolean]) => assets.filter(assetFilter)),
        tap((assets: TAsset[]) => {
          const assetToRemove = assets
            .filter((asset: TAsset) => !asset.positionDeleted)
            .map((asset: TAsset) => asset.id);
          this._removeDeletedAssets(assetToRemove);
        }),
        concatAll(),
        filter((asset: TAsset) => {
          return !!asset && !!asset.transformation?.position && !asset.positionDeleted;
        }),

        mergeMap((asset: TAsset) => {
          return iif(
            () => !!this._nodes[asset.id],
            of(true).pipe(
              tap(() => this.updateState(asset)),
              take(1)
            ),
            of(true).pipe(
              switchMap(() => this.create3DComponent$(asset)),
              take(1),
              catchError((e) => {
                console.error(e);
                return of(e);
              })
            )
          ).pipe(
            take(1),
            catchError((e) => {
              console.error(e);
              return of(e);
            })
          );
        })
      )
    )
  );

  removeDeletedAsset(id: string) {
    this.scanManagerService.component.deleteNote(id);

    if (this._nodes[id]) {
      delete this._nodes[id];
    }
  }

  create3DComponent$(asset: TAsset) {
    return this._markerIconService.markerIcons$(asset.category).pipe(
      take(1),
      switchMap((icons: MarkerIcons) => {
        const component: ComponentConfiguration = {
          id: asset.id,
          normal: asset.transformation!.normalVector as Vector3,
          stemHeight: asset.transformation!.stemLength!,
          position: asset.transformation!.position as Vector3,
          scale: { x: 0.14, y: 0.14, z: 0.14 } as Vector3,
          up: new Vector3(0, 0, 1),
          userData: {
            type: 'assets',
            assetId: asset.id
          },
          tooltip: { component: AssetTooltipComponent },
          autoScale: true,
          objects: [
            new SpriteConfiguration({
              icon: icons.default,
              // shown while the asset is open on the side sheet; the icon itself stays opaque so it
              // keeps rendering above Matterport's sweep pucks
              selectedHalo: icons.selected
            })
          ]
        };

        return this.runExclusiveMarkerOp$(asset.id, () => {
          this.scanManagerService.component.deleteNote(asset.id);
          return this.scanManagerService.component.addComponentWithOffset$(component).pipe(
            tap((component: MatterportComponent) => {
              this._nodes[asset.id] = component;
            }),
            catchError((e) => {
              console.error(e);
              return of(e);
            })
          );
        });
      })
    );
  }

  updateState(asset: TAsset) {
    const component = this._nodes[asset.id];

    this.scanManagerService.component.updatePositionWithOffset({
      id: asset.id,
      normal: asset.transformation!.normalVector as Vector3,
      position: asset.transformation!.position as Vector3,
      stemHeight: asset.transformation!.stemLength!,
      scale: { x: 0.14, y: 0.14, z: 0.14 } as Vector3
    });

    this._markerIconService
      .markerIcons$(asset.category)
      .pipe(take(1))
      .subscribe((icons: MarkerIcons) => {
        const sprite = component.children.find((comp) => comp instanceof SpriteComponent);
        if (!(sprite instanceof SpriteComponent)) return;
        sprite.icon = icons.default;
        sprite.selectedHalo = icons.selected;
      });
  }

  private _removeDeletedAssets(assets: string[]) {
    const currentAssets = Object.keys(this._nodes);
    if (currentAssets.length > 0) {
      const collection = [...currentAssets.filter((x) => !assets.includes(x))];
      collection.forEach((nodeId) => {
        if (this._nodes[nodeId]) {
          this.scanManagerService.component.deleteNote(nodeId);
          delete this._nodes[nodeId];
        }
      });
    }
  }
}
